import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { MapPin, Clock, Users, Lock, ExternalLink, CalendarDays } from "lucide-react";
import { TEST_IDS } from "@/constants/testIds";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { format } from "date-fns";
import PaymentMethodPicker from "@/components/PaymentMethodPicker";
import BookingConfirmation from "@/components/BookingConfirmation";
import { LOGO_URL } from "@/components/Navbar";
import { useLanguage } from "@/contexts/LanguageContext";

export default function BookCourt() {
  const { user } = useAuth();
  const { t, dateFnsLocale } = useLanguage();
  const navigate = useNavigate();
  const [club, setClub] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [booking, setBooking] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const bookingRef = useRef(null);

  const openBooking = () => {
    setShowBooking(true);
    setTimeout(() => bookingRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };

  useEffect(() => { api.get("/club").then(r => setClub(r.data)).catch(() => {}); }, []);

  const loadSlots = useCallback(() => {
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    api.get(`/availability?date=${dateStr}`).then(r => { setSlots(r.data.slots || []); setSelectedSlot(null); }).catch(() => setSlots([]));
  }, [selectedDate]);

  useEffect(() => { loadSlots(); }, [loadSlots]);

  const handleBook = async () => {
    if (!user) { navigate("/login"); return; }
    if (!selectedSlot) return;
    setBooking(true);
    try {
      const res = await api.post("/bookings", { date: format(selectedDate, "yyyy-MM-dd"), start_time: selectedSlot.start, payment_method: paymentMethod });
      setConfirmed(res.data);
      loadSlots();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      loadSlots();
    } finally {
      setBooking(false);
    }
  };

  if (!club) return <div className="min-h-[60vh] flex items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-coral rounded-full border-t-transparent" /></div>;

  const courtPrice = club.court_price || 1000;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8" data-testid="book-court-page">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="relative h-56 sm:h-72 rounded-2xl overflow-hidden mb-8">
          <img src={club.images?.[0]} alt={club.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-midnight-500/90 via-midnight-500/30 to-transparent" />
          <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src={club.logo_url || LOGO_URL} alt={club.name} className="h-16 w-16 rounded-xl shadow-lg bg-white/90 p-1" />
              <a href={club.maps_url || "#"} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-white text-base sm:text-lg font-medium hover:text-coral transition-colors" data-testid="club-address-link">
                <MapPin size={16} /> {club.address} <ExternalLink size={12} />
              </a>
            </div>
          </div>
        </div>

        <div className={`grid grid-cols-1 gap-8 ${showBooking ? "lg:grid-cols-3" : "max-w-xl mx-auto"}`}>
          <div className="lg:col-span-1 space-y-4">
            <Card className="border-border/50">
              <CardContent className="p-6 space-y-4">
                <p className="text-sm text-foreground">{club.description}</p>
                <div className="space-y-2.5 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2"><Clock size={16} className="text-teal" /> {t("book.open")} <span className="text-teal font-semibold">{club.opening_hours.replace("-", " – ")}</span> · {t("book.sessions")}</div>
                  <div className="flex items-center gap-2"><Users size={16} className="text-golden" /> {t("book.players")}</div>
                  <div className="flex items-center gap-2"><Lock size={16} className="text-coral" /> {t("book.one_booker")}</div>
                </div>
                <div className="pt-4 border-t border-border/50 flex items-end justify-between">
                  <p className="text-3xl font-black text-golden">Rs {courtPrice.toLocaleString()} <span className="text-muted-foreground font-semibold">{t("book.per_hour")}</span></p>
                  <div className="text-center">
                    <p className="text-lg font-bold">Rs {(club.price_per_slot || courtPrice / 4).toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">{t("book.split")}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            {!showBooking && (
              <Button onClick={openBooking} className="w-full rounded-full bg-coral hover:bg-coral-600 text-white h-12 text-base font-semibold shadow-lg shadow-coral/25" data-testid="open-booking-btn">
                <CalendarDays size={18} className="mr-2" /> {t("book.btn")}
              </Button>
            )}
          </div>

          {showBooking && (
          <motion.div ref={bookingRef} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-2">
            <Card className="border-border/50" data-testid={TEST_IDS.BOOKING_CALENDAR}>
              <CardHeader><CardTitle className="text-xl">{t("book.title")}</CardTitle></CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm font-medium mb-2">{t("book.select_date")}</p>
                    <Calendar mode="single" locale={dateFnsLocale} selected={selectedDate} onSelect={d => d && setSelectedDate(d)}
                      disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))} className="rounded-xl border border-border/50" />
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-2">{t("book.slots")} · {format(selectedDate, "EEE d MMM", { locale: dateFnsLocale })}</p>
                    <div className="grid grid-cols-2 gap-2" data-testid="slots-grid">
                      {slots.map(slot => (
                        <SlotButton key={slot.start} t={t} slot={slot} selected={selectedSlot?.start === slot.start} onSelect={() => setSelectedSlot(slot)} />
                      ))}
                    </div>
                    {slots.length > 0 && slots.every(s => !s.available) && (
                      <p className="text-xs text-muted-foreground mt-3 text-center">{t("book.no_slots")}</p>
                    )}
                  </div>
                </div>

                {selectedSlot && (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="p-5 rounded-2xl bg-teal/5 border border-teal/20 space-y-4" data-testid="booking-summary">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-heading font-bold">{club.court?.name || t("book.court")} · {format(selectedDate, "EEE d MMM", { locale: dateFnsLocale })}</p>
                        <p className="text-sm text-muted-foreground">{t("book.summary_sub", { start: selectedSlot.start, end: selectedSlot.end })}</p>
                      </div>
                      <p className="text-2xl font-black text-golden">Rs {courtPrice.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-2">{t("book.how_pay")}</p>
                      <PaymentMethodPicker value={paymentMethod} onChange={setPaymentMethod} />
                    </div>
                    <Button data-testid={TEST_IDS.BOOKING_CONFIRM} onClick={handleBook} disabled={booking}
                      className="w-full rounded-full bg-coral hover:bg-coral-600 text-white h-11 font-semibold">
                      {booking ? t("book.reserving") : user ? t("book.reserve", { price: courtPrice.toLocaleString() }) : t("book.signin")}
                    </Button>
                    <p className="text-[11px] text-muted-foreground text-center" data-testid="cancellation-policy">{t("book.policy")}</p>
                  </motion.div>
                )}
              </CardContent>
            </Card>
          </motion.div>
          )}
        </div>
      </motion.div>

      <BookingConfirmation open={!!confirmed} onClose={() => setConfirmed(null)} booking={confirmed} club={club} kind="court" />
    </div>
  );
}

function SlotButton({ slot, selected, onSelect, t }) {
  const blockedLabel = slot.blocked_by === "coaching" ? t("book.slot_lesson") : slot.blocked_by === "tournament" ? t("book.slot_tournament") : slot.blocked_by === "booking" ? t("book.slot_booked") : slot.past ? t("book.slot_past") : "";
  return (
    <button
      data-testid={TEST_IDS.BOOKING_SLOT}
      data-slot={slot.start}
      onClick={() => slot.available && onSelect()}
      disabled={!slot.available}
      className={`p-2.5 rounded-xl text-sm font-medium text-center transition-all ${
        selected ? "bg-coral text-white ring-2 ring-coral/30"
        : slot.available ? "bg-muted/50 hover:bg-muted text-foreground border border-border/50"
        : "bg-muted/20 text-muted-foreground/50 cursor-not-allowed"
      }`}
    >
      <Clock size={12} className="inline mr-1" />{slot.start}
      {!slot.available && <span className="block text-[10px] mt-0.5 uppercase tracking-wide">{blockedLabel}</span>}
    </button>
  );
}
